import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiError, authExtrasApi } from '../services/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { SignIn, MicrosoftOutlookLogo, Warning } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [entraLoading, setEntraLoading] = useState(false);
  const [entraConfigured, setEntraConfigured] = useState(false);
  const [registrationAllowed, setRegistrationAllowed] = useState(true);

  useEffect(() => {
    // Check Entra ID status and registration status
    const checkStatus = async () => {
      try {
        const [entraRes, regRes] = await Promise.all([
          authExtrasApi.entraStatus().catch(() => ({ data: { configured: false } })),
          authExtrasApi.registrationStatus().catch(() => ({ data: { allowed: true } })),
        ]);
        setEntraConfigured(entraRes.data.configured);
        setRegistrationAllowed(regRes.data.allowed);
      } catch (e) { /* ignore */ }
    };
    checkStatus();
    // Check for error from callback
    const errParam = searchParams.get('error');
    if (errParam) setError(decodeURIComponent(errParam));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleEntraIdLogin = async () => {
    if (!entraConfigured) {
      setError('Accesso con Entra ID non ancora configurato. Contatta l\'amministratore.');
      return;
    }
    setEntraLoading(true);
    try {
      const { data } = await authExtrasApi.entraLogin();
      if (data.auth_url) {
        window.location.href = data.auth_url;
      }
    } catch (err) {
      setError(formatApiError(err));
      setEntraLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1610741804272-059e1d3c5dba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBhYnN0cmFjdCUyMGFyY2hpdGVjdHVyYWx8ZW58MHx8fHwxNzc1OTk4MTE2fDA&ixlib=rb-4.1.0&q=85)',
          backgroundColor: '#0A0A0A',
        }}
      >
        <div className="flex flex-col justify-end p-12 bg-gradient-to-t from-black/80 to-transparent w-full">
          <h1 className="font-heading text-4xl font-black text-white tracking-tighter mb-4">
            DCO Planner
          </h1>
          <p className="text-white/70 text-lg max-w-md">
            Gestisci turni, approvazioni e coordinamento del team in un'unica piattaforma.
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-12">
            <div className="flex flex-col items-start">
              <img src="/logo-s2e.png" alt="DCO Planner" className="h-8 w-auto object-contain" />
              <div className="flex justify-between w-full mt-0.5">
                {'DCO PLANNER'.split('').map((ch, i) => (
                  <span key={i} className="font-heading text-[9px] font-bold text-[#0A0A0A]">{ch === ' ' ? '\u00A0' : ch}</span>
                ))}
              </div>
            </div>
          </div>

          <h2 className="font-heading text-3xl font-bold tracking-tight mb-2">Bentornato</h2>
          <p className="text-[#52525B] mb-8">Accedi al tuo account per continuare</p>

          {error && (
            <div className="bg-[#FF2A00]/5 border border-[#FF2A00] p-4 mb-6 flex items-start gap-3" data-testid="login-error">
              <Warning className="text-[#FF2A00] flex-shrink-0 mt-0.5" size={20} weight="bold" />
              <p className="text-[#FF2A00] text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                Indirizzo Email
              </Label>
              <Input
                id="email" type="email" value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="nome@azienda.com"
                className="border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] rounded-none"
                data-testid="login-email-input" required
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-bold text-[#52525B] mb-2 block">
                Password
              </Label>
              <Input
                id="password" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Inserisci la password"
                className="border border-[#E4E4E7] bg-white px-4 py-3 text-sm focus:border-[#0A0A0A] focus:ring-1 focus:ring-[#0A0A0A] rounded-none"
                data-testid="login-password-input" required
              />
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full bg-[#002FA7] text-white px-6 py-3 font-medium hover:bg-[#00278A] focus:ring-2 focus:ring-[#002FA7]/30 transition-colors rounded-none"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></span>
                  Accesso in corso...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <SignIn size={20} weight="bold" />
                  Accedi
                </span>
              )}
            </Button>
          </form>

          {entraConfigured && (
            <>
              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E4E4E7]"></div>
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-white px-4 text-[#52525B] tracking-[0.2em] font-bold">Oppure accedi con</span>
                </div>
              </div>

              <Button
                type="button" onClick={handleEntraIdLogin} disabled={entraLoading}
                variant="outline"
                className="w-full bg-white text-[#0A0A0A] border border-[#0A0A0A] px-6 py-3 font-medium hover:bg-[#F4F4F5] transition-colors rounded-none"
                data-testid="login-entra-id-btn"
              >
                {entraLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin w-4 h-4 border-2 border-[#0A0A0A] border-t-transparent rounded-full"></span>
                    Reindirizzamento...
                  </span>
                ) : (
                  <>
                    <MicrosoftOutlookLogo size={20} weight="bold" className="mr-2" />
                    Accedi con Microsoft Entra ID
                  </>
                )}
              </Button>
            </>
          )}

          {registrationAllowed && (
            <p className="text-center text-sm text-[#52525B] mt-8">
              Non hai un account?{' '}
              <Link to="/register" className="text-[#002FA7] font-semibold hover:underline" data-testid="register-link">
                Crea account
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
